import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import {
  analyzePrompt,
  renderPromptPreview,
  type PromptAnalysis,
} from "@/lib/prompt-template";
import {
  AlertTriangle,
  BookOpen,
  MessageSquare,
  Plus,
} from "lucide-react";

/** Sample values so a preview of `{% if customer_number %}` matches a real call. */
const PREVIEW_SYSTEM_VALUES = {
  customer_number: "+15551234567",
  assistant_number: "+15559876543",
};

const SNIPPETS: Array<{ label: string; hint: string; text: string }> = [
  {
    label: "Fallback value",
    hint: "Shortest option — use this when you just need a stand-in word.",
    text: `Hi {{ first_name | default: "there" }},`,
  },
  {
    label: "Different line if missing",
    hint: "Two complete alternatives. This is the one that replaces thinking-level if/else.",
    text:
      `{% if first_name %}\n` +
      `Kya main {{ first_name }} ji se baat kar rahi hoon?\n` +
      `{% else %}\n` +
      `Hello, kya main aapka naam jaan sakti hoon?\n` +
      `{% endif %}`,
  },
  {
    label: "Extra instruction only if missing",
    hint: "Adds a step without duplicating the whole line.",
    text: `{% unless account_id %}\nAsk the caller for their account number first.\n{% endunless %}`,
  },
  {
    label: "Branch on a value",
    hint: "Compare against text in quotes — every variable arrives as text.",
    text: `{% if plan == "pro" %}\nOffer the priority slot.\n{% else %}\nOffer the standard slot.\n{% endif %}`,
  },
];

interface SystemPromptEditorProps {
  value: string;
  onChange: (value: string) => void;
  /** Bubble the analysis up so Publish can gate on a syntax error. */
  onAnalysisChange?: (analysis: PromptAnalysis) => void;
  /** Scopes the remembered preview values. */
  agentId?: string;
}

export function SystemPromptEditor({
  value,
  onChange,
  onAnalysisChange,
  agentId,
}: SystemPromptEditorProps) {
  const promptRef = useRef<HTMLTextAreaElement>(null);
  const pendingInsert = useRef<string | null>(null);
  const [tab, setTab] = useState("edit");
  const [newVarName, setNewVarName] = useState("");
  const [addVarOpen, setAddVarOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [previewValues, setPreviewValues] = useState<Record<string, string>>({});

  // Debounced so we aren't parsing the whole prompt on every keystroke.
  const [analyzed, setAnalyzed] = useState<string>(value);
  useEffect(() => {
    const t = setTimeout(() => setAnalyzed(value), 200);
    return () => clearTimeout(t);
  }, [value]);

  const analysis = useMemo(() => analyzePrompt(analyzed), [analyzed]);
  useEffect(() => onAnalysisChange?.(analysis), [analysis, onAnalysisChange]);

  const storageKey = agentId ? `verbeo:prompt-preview:${agentId}` : null;
  useEffect(() => {
    if (!storageKey) return;
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) setPreviewValues(JSON.parse(saved));
    } catch {
      /* a corrupt entry just means we start from blank values */
    }
  }, [storageKey]);

  const setPreviewValue = (name: string, next: string) => {
    setPreviewValues((prev) => {
      const merged = { ...prev, [name]: next };
      if (storageKey) {
        try {
          localStorage.setItem(storageKey, JSON.stringify(merged));
        } catch {
          /* private mode / quota — the preview just won't be remembered */
        }
      }
      return merged;
    });
  };

  // Radix unmounts the inactive tab, so the textarea's own selection resets to
  // 0 every time you visit Preview. Without remembering it here, inserting a
  // snippet from the guide lands at the very top of the prompt and jams into
  // whatever was on line 1.
  const caretRef = useRef<number | null>(null);
  const rememberCaret = () => {
    const el = promptRef.current;
    if (el) caretRef.current = el.selectionStart ?? null;
  };

  const insertAtCursor = useCallback(
    (rawText: string) => {
      const el = promptRef.current;
      const caret = caretRef.current;

      // A multi-line block should land as its own paragraph rather than being
      // glued onto the end of the line the caret happened to be on.
      const surround = (at: number, source: string) => {
        if (!rawText.includes("\n")) return rawText;
        const before = at > 0 && source[at - 1] !== "\n" ? "\n" : "";
        const after = at < source.length && source[at] !== "\n" ? "\n" : "";
        return before + rawText + after;
      };

      if (!el) {
        onChange(value + surround(value.length, value));
        return;
      }

      // An unfocused textarea reports selectionStart 0, so trusting it would
      // silently prepend to the prompt — including the very first insert on a
      // page where nobody has clicked into the editor yet. Only believe the
      // live selection while the element actually has focus; otherwise use the
      // remembered caret, and fall back to the END rather than the start.
      const start = Math.min(
        document.activeElement === el
          ? el.selectionStart ?? el.value.length
          : caret ?? el.value.length,
        el.value.length,
      );
      el.setSelectionRange(start, start);
      el.focus();
      const text = surround(start, el.value);

      // execCommand is deprecated but it is the only way to insert text and
      // keep the browser's native undo stack. Rebuilding the string through
      // setState wipes Ctrl+Z, which in a 2000-word prompt editor is a real
      // defect, not a nitpick.
      const ok = document.execCommand?.("insertText", false, text);
      if (!ok) {
        const end = el.selectionEnd ?? start;
        onChange(value.slice(0, start) + text + value.slice(end));
        requestAnimationFrame(() =>
          el.setSelectionRange(start + text.length, start + text.length),
        );
      }
      caretRef.current = start + text.length;
    },
    [onChange, value],
  );

  /** Insert from inside the guide dialog: close first, then write at the caret. */
  const insertFromGuide = (text: string) => {
    pendingInsert.current = text;
    setTab("edit");
    setGuideOpen(false);
  };

  /** Jump the caret to wherever the parser gave up. */
  const focusError = () => {
    setTab("edit");
    requestAnimationFrame(() => {
      const el = promptRef.current;
      if (!el || !analysis.error) return;
      el.focus();
      el.setSelectionRange(analysis.error.offset, analysis.error.offset);
    });
  };

  const preview = useMemo(
    () =>
      renderPromptPreview(value, {
        ...PREVIEW_SYSTEM_VALUES,
        ...previewValues,
      }),
    [value, previewValues],
  );

  const suppliedCount = analysis.variables.filter((v) =>
    (previewValues[v] ?? "").trim(),
  ).length;

  const requiredCount = analysis.required.length;
  const optionalCount = analysis.optional.length;

  return (
    <div
      className={cn(
        // overflow-hidden is load-bearing: without it, a tall footer squeezes
        // the textarea to zero height and its text renders over the toolbar.
        "flex-1 flex flex-col h-full min-h-0 overflow-hidden rounded-xl border-2 bg-card shadow-md transition-colors",
        analysis.error ? "border-destructive/60" : "border-border",
      )}
    >
      <Tabs
        value={tab}
        onValueChange={setTab}
        className="flex flex-col h-full min-h-0"
      >
        {/* Header */}
        <div className="flex-none px-5 py-4 border-b border-border bg-gradient-to-br from-primary/5 to-transparent">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <Label
                htmlFor="system-prompt"
                className="text-base font-semibold flex items-center gap-2"
              >
                <MessageSquare className="h-4 w-4 text-primary" />
                System Prompt
              </Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                Define the agent's role, purpose, objectives, and response guidelines
              </p>
            </div>
            <TabsList className="flex-none h-8">
              <TabsTrigger value="edit" className="text-xs px-3">
                Edit
              </TabsTrigger>
              <TabsTrigger value="preview" className="text-xs px-3">
                Preview
              </TabsTrigger>
            </TabsList>
          </div>
        </div>

        {/* Toolbar — capped height so it can never crowd out the editor */}
        <div className="flex-none border-b border-border bg-muted/20 px-5 py-2.5">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-baseline gap-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              Variables
              {analysis.variables.length > 0 && (
                <span className="normal-case tracking-normal text-muted-foreground/70">
                  {requiredCount} required
                  {optionalCount > 0 && ` · ${optionalCount} optional`}
                </span>
              )}
            </div>
            <div className="flex flex-none items-center gap-1">
              <Popover open={addVarOpen} onOpenChange={setAddVarOpen}>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-7 gap-1 px-2 text-xs">
                    <Plus className="h-3.5 w-3.5" />
                    Variable
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64" align="end">
                  <Label htmlFor="new-var" className="text-xs">
                    Variable name
                  </Label>
                  <div className="mt-2 flex gap-2">
                    <Input
                      id="new-var"
                      value={newVarName}
                      onChange={(e) => setNewVarName(e.target.value)}
                      placeholder="first_name"
                      className="h-8 font-mono text-sm"
                      onKeyDown={(e) => {
                        if (e.key !== "Enter") return;
                        e.preventDefault();
                        if (!/^[A-Za-z_]\w*$/.test(newVarName)) return;
                        insertAtCursor(`{{ ${newVarName} }}`);
                        setNewVarName("");
                        setAddVarOpen(false);
                      }}
                    />
                    <Button
                      size="sm"
                      className="h-8"
                      disabled={!/^[A-Za-z_]\w*$/.test(newVarName)}
                      onClick={() => {
                        insertAtCursor(`{{ ${newVarName} }}`);
                        setNewVarName("");
                        setAddVarOpen(false);
                      }}
                    >
                      Insert
                    </Button>
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Letters, numbers and underscores only.
                  </p>
                </PopoverContent>
              </Popover>

              <Dialog open={guideOpen} onOpenChange={setGuideOpen}>
                <DialogTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-7 gap-1 px-2 text-xs">
                    <BookOpen className="h-3.5 w-3.5" />
                    Guide
                  </Button>
                </DialogTrigger>
                <DialogContent
                  className="max-w-2xl max-h-[85vh] overflow-y-auto"
                  // Keep focus off the trigger on close so the pending snippet
                  // lands at the caret in the textarea instead.
                  onCloseAutoFocus={(e) => {
                    if (!pendingInsert.current) return;
                    e.preventDefault();
                    const text = pendingInsert.current;
                    pendingInsert.current = null;
                    requestAnimationFrame(() => insertAtCursor(text));
                  }}
                >
                  <DialogHeader>
                    <DialogTitle>Variables &amp; conditions</DialogTitle>
                    <DialogDescription>
                      Write <code className="font-mono">{"{{ first_name }}"}</code> to
                      drop a value into the prompt, and a condition to change what the
                      agent says when a value is missing.
                    </DialogDescription>
                  </DialogHeader>

                  <div className="rounded-lg border border-primary/20 bg-primary/5 p-3">
                    <p className="text-sm">
                      <strong className="font-semibold">The one rule:</strong> a
                      variable counts as supplied only if it has a non-empty value. A
                      blank value behaves exactly like a missing one, so{" "}
                      <code className="font-mono text-xs">{"{% if name %}"}</code>{" "}
                      already means "we have a real name".
                    </p>
                  </div>

                  <div className="space-y-2">
                    {SNIPPETS.map((s) => (
                      <div
                        key={s.label}
                        className="rounded-lg border bg-muted/30 p-3 transition-colors hover:border-primary/40"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-medium">{s.label}</p>
                            <p className="mt-0.5 text-xs text-muted-foreground">
                              {s.hint}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 flex-none px-2.5 text-xs"
                            onClick={() => insertFromGuide(s.text)}
                          >
                            Insert
                          </Button>
                        </div>
                        <pre className="mt-2 overflow-x-auto rounded bg-background/70 p-2 font-mono text-[11px] leading-relaxed">
                          {s.text}
                        </pre>
                      </div>
                    ))}
                  </div>

                  <div className="space-y-1.5 text-xs text-muted-foreground">
                    <p>
                      <strong className="text-foreground">Filled in for you</strong> —
                      never supply these:{" "}
                      <code className="font-mono">{"{{ customer_number }}"}</code>,{" "}
                      <code className="font-mono">{"{{ assistant_number }}"}</code>,{" "}
                      <code className="font-mono">
                        {"{{ current_time_Asia/Kolkata }}"}
                      </code>
                      .
                    </p>
                    <p>
                      <strong className="text-foreground">Blank lines</strong> — a
                      condition on its own line leaves one behind. Write{" "}
                      <code className="font-mono">{"{%- if x -%}"}</code> to trim it.
                    </p>
                    <p>
                      <strong className="text-foreground">Scope</strong> — conditions
                      work in the system prompt only, and variable names must be
                      letters, numbers and underscores.
                    </p>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {analysis.variables.length > 0 ? (
            // Two full rows then scroll — tall enough that a second row is
            // never sliced in half, short enough that 30 variables cannot push
            // the editor off screen.
            <div className="mt-2 flex max-h-[3.5rem] flex-wrap content-start gap-1.5 overflow-y-auto pr-1">
              {analysis.variables.map((name) => {
                const optional = analysis.optional.includes(name);
                return (
                  <button
                    key={name}
                    type="button"
                    onClick={() => insertAtCursor(`{{ ${name} }}`)}
                    title={
                      optional
                        ? `${name} — only used inside a condition, safe to leave unset. Click to insert.`
                        : `${name} — required, the call needs a value. Click to insert.`
                    }
                  >
                    <Badge
                      variant={optional ? "outline" : "secondary"}
                      className="cursor-pointer font-mono text-[11px] font-normal hover:bg-primary/10"
                    >
                      {name}
                      {optional && (
                        <span className="ml-1 not-italic opacity-50">opt</span>
                      )}
                    </Badge>
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="mt-1 text-xs text-muted-foreground">
              None yet — add one to personalise what the agent says.
            </p>
          )}
        </div>

        {/* Editor */}
        <TabsContent value="edit" className="flex-1 min-h-0 mt-0">
          <Textarea
            id="system-prompt"
            ref={promptRef}
            placeholder="Enter your system prompt here..."
            value={value}
            onChange={(e) => {
              onChange(e.target.value);
              rememberCaret();
            }}
            onSelect={rememberCaret}
            onKeyUp={rememberCaret}
            onClick={rememberCaret}
            onBlur={rememberCaret}
            className="h-full w-full resize-none rounded-none border-0 bg-transparent px-5 py-4 text-[15px] leading-relaxed focus-visible:ring-0"
          />
        </TabsContent>

        {/* Preview */}
        {/* Side by side so the rendered output — the thing you came here to
            read — stays on screen no matter how many variables the prompt has.
            Each pane scrolls independently. */}
        <TabsContent
          value="preview"
          className="flex-1 min-h-0 mt-0 overflow-hidden"
        >
          <div
            className={cn(
              "grid h-full min-h-0 gap-4 px-5 py-4",
              analysis.variables.length > 0
                ? "grid-cols-1 lg:grid-cols-[minmax(13rem,17rem)_1fr]"
                : "grid-cols-1",
            )}
          >
            {analysis.variables.length > 0 && (
              <div className="flex min-h-0 flex-col rounded-lg border bg-muted/30">
                <div className="flex flex-none items-center justify-between gap-2 border-b px-3 py-2">
                  <Label className="text-xs font-semibold">Test values</Label>
                  <Badge variant="secondary" className="text-[10px] font-normal">
                    {suppliedCount}/{analysis.variables.length}
                  </Badge>
                </div>
                <div className="min-h-0 flex-1 space-y-2 overflow-y-auto px-3 py-2.5">
                  {analysis.variables.map((name) => (
                    <div key={name}>
                      <span className="mb-1 block truncate font-mono text-[10px] text-muted-foreground">
                        {name}
                        {analysis.optional.includes(name) && (
                          <span className="ml-1 opacity-60">opt</span>
                        )}
                      </span>
                      <Input
                        value={previewValues[name] ?? ""}
                        onChange={(e) => setPreviewValue(name, e.target.value)}
                        placeholder="(not supplied)"
                        className="h-7 text-xs"
                      />
                    </div>
                  ))}
                </div>
                <p className="flex-none border-t px-3 py-2 text-[11px] leading-snug text-muted-foreground">
                  Empty means not supplied — the same rule the call uses. Clear a box
                  to see the {"{% else %}"} branch.
                </p>
              </div>
            )}

            <div className="flex min-h-0 flex-col">
              {preview.error ? (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    Line {preview.error.row}, column {preview.error.col}:{" "}
                    {preview.error.message}
                  </AlertDescription>
                </Alert>
              ) : (
                <>
                  <p className="mb-1.5 flex-none text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                    What the agent will be given
                  </p>
                  <pre className="min-h-0 flex-1 overflow-auto whitespace-pre-wrap break-words rounded-lg border bg-muted/30 p-4 font-mono text-[13px] leading-relaxed">
                    {preview.text ||
                      "(the prompt renders to nothing with these values)"}
                  </pre>
                  <p className="mt-2 flex-none text-[11px] text-muted-foreground">
                    Your prompt only — the knowledge-base section and call data notes
                    are added at call time.
                  </p>
                </>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Diagnostics — only rendered when there is something to say, and
            capped so a noisy prompt cannot squeeze the editor. */}
        {(analysis.error || analysis.warnings.length > 0) && (
          <div className="max-h-28 flex-none space-y-1.5 overflow-y-auto border-t border-border bg-muted/20 px-5 py-2.5">
            {analysis.error && (
              <Alert
                variant="destructive"
                className="cursor-pointer border-0 bg-destructive/10 py-2"
                onClick={focusError}
              >
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  Line {analysis.error.row}, column {analysis.error.col}:{" "}
                  {analysis.error.message}
                  <span className="ml-1 opacity-70">— click to jump there</span>
                </AlertDescription>
              </Alert>
            )}
            {analysis.warnings.slice(0, 4).map((w) => (
              <div
                key={`${w.rule}-${w.offset}`}
                className="flex gap-2 text-xs text-amber-700 dark:text-amber-500"
              >
                <AlertTriangle className="mt-0.5 h-3 w-3 flex-none" />
                <span>{w.message}</span>
              </div>
            ))}
          </div>
        )}
      </Tabs>
    </div>
  );
}
